import { del, get, patch, post, put } from "@/api/client";
import type { SessionUser } from "@/api/auth";

export interface Branding {
  accent?: string;
  accent_dark?: string;
  brand_name?: string;
}

export interface BrandingInput {
  accent?: string;
  accent_dark?: string;
  brand_name?: string;
  locale?: "nl" | "en";
}

export type TenantStatus = "trial" | "active" | "suspended";

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  created_at: string;
  plan: string;
  seat_limit: number | null;
  status: TenantStatus;
  trial_ends_at: string | null;
  notes: string | null;
  seats_used?: number | null; // only on the platform overview list
}

export interface SubscriptionInput {
  plan: string;
  seat_limit: number | null;
  status: TenantStatus;
  trial_ends_at?: string | null;
  notes?: string | null;
}

export interface CreateTenantInput {
  name: string;
  slug: string;
  admin_email: string;
  admin_password: string;
  admin_full_name?: string | null;
}

export async function listTenants(signal?: AbortSignal): Promise<Tenant[]> {
  const r = await get<Tenant[]>("/api/tenants", signal);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function createTenant(
  input: CreateTenantInput,
): Promise<{ tenant: Tenant; admin: SessionUser }> {
  const r = await post<{ tenant: Tenant; admin: SessionUser }>("/api/tenants", input);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function renameTenant(id: string, name: string): Promise<Tenant> {
  const r = await patch<Tenant>(`/api/tenants/${id}`, { name });
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function updateSubscription(id: string, input: SubscriptionInput): Promise<Tenant> {
  const r = await patch<Tenant>(`/api/tenants/${id}/subscription`, input);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function deleteTenant(id: string): Promise<void> {
  const r = await del(`/api/tenants/${id}`);
  if (!r.ok) throw new Error(r.err.message);
}

export async function listTenantUsers(id: string, signal?: AbortSignal): Promise<SessionUser[]> {
  const r = await get<SessionUser[]>(`/api/tenants/${id}/users`, signal);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function fetchCurrentTenant(signal?: AbortSignal): Promise<Tenant> {
  const r = await get<Tenant>("/api/tenants/current", signal);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export async function updateBranding(input: BrandingInput): Promise<Tenant> {
  const r = await put<Tenant>("/api/tenants/current/branding", input);
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}
